/* Beskytter konfiguratorerne mod inaktive EmailWish-overlays.

   EmailWish maa gerne vise en rigtig popup. Problemet er alene det transparente
   ``.popup-overlay``, der kan blive staaende med pointer-events:auto uden en
   synlig dialog. Derfor bevares DOM'en, og kun det inaktive overlay faar en
   reversibel pointer-/fokus-neutralisering. */
(() => {
  'use strict';

  const CONFIGURATOR_SELECTOR = '[data-nf-city-builder], [data-nf-frame-config]';
  const ENABLED_SELECTOR = '[data-nf-konfigurator-fokus="true"]';
  const EMAILWISH_ROOT_SELECTOR = '#ew-root-container, .ew-popup-css';
  const OVERLAY_SELECTOR = '.popup-overlay';
  const DIALOG_SELECTOR = '.popup-wrapper, [role="dialog"], [aria-modal="true"]';
  const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
  const STYLE_ID = 'nf-konfigurator-side-style';

  const existing = window.nfKonfiguratorFokus;
  if (existing?.active) {
    existing.refresh();
    return;
  }

  if (!document.querySelector(CONFIGURATOR_SELECTOR)) return;

  let active = true;
  let scheduled = false;
  let reconcileCount = 0;
  let roots = [];
  const neutralized = new Map();

  function ensurePageScope() {
    document.documentElement.classList.add('nf-configurator-side');
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = '@media(max-width:749px){html.nf-configurator-side .nf-floating-cart{display: none !important;}}';
    document.head.append(style);
  }

  function colorAlpha(color) {
    if (!color || color === 'transparent') return 0;
    const rgba = color.match(/^rgba?\(([^)]+)\)$/i);
    if (!rgba) return 1;
    const parts = rgba[1].split(/[\s,/]+/).filter(Boolean);
    return parts.length > 3 ? Number(parts[3]) : 1;
  }

  function isVisible(node) {
    if (!(node instanceof HTMLElement) || node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
    const style = getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    if (Number(style.opacity || 1) <= 0.01) return false;
    const rect = node.getBoundingClientRect();
    return node.getClientRects().length > 0 && rect.width > 1 && rect.height > 1;
  }

  function hasVisibleDialog() {
    return roots.some(root => {
      if (root.matches?.(DIALOG_SELECTOR) && isVisible(root)) return true;
      return [...root.querySelectorAll(DIALOG_SELECTOR)].some(isVisible);
    });
  }

  function isInactiveOverlay(overlay, dialogVisible) {
    if (!(overlay instanceof HTMLElement) || dialogVisible || !isVisible(overlay)) return false;
    const style = getComputedStyle(overlay);
    if (style.pointerEvents === 'none' && !neutralized.has(overlay)) return false;
    const transparent = colorAlpha(style.backgroundColor) <= 0.01 && style.backgroundImage === 'none';
    return transparent || overlay.getAttribute('aria-hidden') === 'true';
  }

  function neutralize(overlay) {
    if (neutralized.has(overlay)) return;
    neutralized.set(overlay, {
      pointerValue: overlay.style.getPropertyValue('pointer-events'),
      pointerPriority: overlay.style.getPropertyPriority('pointer-events'),
      inertAttribute: overlay.hasAttribute('inert'),
      inertValue: Boolean(overlay.inert)
    });
    overlay.style.setProperty('pointer-events', 'none', 'important');
    overlay.dataset.nfEmailwishInactive = 'true';
    if (overlay.querySelector(FOCUSABLE_SELECTOR)) overlay.inert = true;
  }

  function restore(overlay) {
    const previous = neutralized.get(overlay);
    if (!previous) return;
    if (previous.pointerValue) {
      overlay.style.setProperty('pointer-events', previous.pointerValue, previous.pointerPriority);
    } else {
      overlay.style.removeProperty('pointer-events');
    }
    if (previous.inertAttribute) overlay.inert = previous.inertValue;
    else overlay.removeAttribute('inert');
    delete overlay.dataset.nfEmailwishInactive;
    neutralized.delete(overlay);
  }

  function observeRoots() {
    roots = [...document.querySelectorAll(EMAILWISH_ROOT_SELECTOR)];
    rootObserver.disconnect();
    roots.forEach(root => rootObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'aria-modal', 'role']
    }));
  }

  function reconcile() {
    if (!active) return;
    scheduled = false;
    reconcileCount += 1;
    observeRoots();
    const overlays = new Set();
    roots.forEach(root => {
      if (root.matches?.(OVERLAY_SELECTOR)) overlays.add(root);
      root.querySelectorAll(OVERLAY_SELECTOR).forEach(overlay => overlays.add(overlay));
    });
    [...neutralized.keys()].forEach(overlay => {
      if (!overlays.has(overlay)) restore(overlay);
    });
    const dialogVisible = hasVisibleDialog();
    overlays.forEach(overlay => {
      if (isInactiveOverlay(overlay, dialogVisible)) neutralize(overlay);
      else restore(overlay);
    });
  }

  function scheduleReconcile() {
    if (!active || scheduled) return;
    scheduled = true;
    queueMicrotask(reconcile);
  }

  const rootObserver = new MutationObserver(scheduleReconcile);
  /* EmailWish-rooten indsaettes som et top-level body-barn. Vi observerer kun
     den liste -- aldrig hele MapLibre/konfigurator-subtree'et. Naar rooten er
     fundet, sker alle detaljerede observationer inde i EmailWish selv. */
  const discoveryObserver = new MutationObserver(scheduleReconcile);

  function refresh() {
    if (!active) return;
    ensurePageScope();
    discoveryObserver.disconnect();
    if (document.querySelector(ENABLED_SELECTOR)) {
      discoveryObserver.observe(document.body, { childList: true });
      scheduleReconcile();
    } else {
      rootObserver.disconnect();
      [...neutralized.keys()].forEach(restore);
    }
  }

  function teardown() {
    if (!active) return;
    active = false;
    scheduled = false;
    rootObserver.disconnect();
    discoveryObserver.disconnect();
    [...neutralized.keys()].forEach(restore);
    window.removeEventListener('pagehide', teardown);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    document.documentElement.classList.remove('nf-configurator-side');
    document.getElementById(STYLE_ID)?.remove();
    api.active = false;
    if (window.nfKonfiguratorFokus === api) delete window.nfKonfiguratorFokus;
  }

  function onSectionUnload() {
    queueMicrotask(() => {
      if (!document.querySelector(CONFIGURATOR_SELECTOR)) teardown();
      else refresh();
    });
  }

  const api = {
    active: true,
    refresh,
    teardown,
    diagnostic: () => ({
      roots: roots.length,
      neutralized: neutralized.size,
      reconciles: reconcileCount
    })
  };
  window.nfKonfiguratorFokus = api;
  window.addEventListener('pagehide', teardown);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  refresh();
})();
